import React, { useEffect, useRef, useState } from "react";
import { WORTHSCORE } from "../mock";
import Reveal from "./Reveal";

const stateColor = { good: "#13D99A", warn: "#F5A623", urgent: "#FF4D4F" };

const ScoreRing = ({ score }) => {
  const ref = useRef(null);
  const [animated, setAnimated] = useState(0);
  const size = 240;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          if (reduce) { setAnimated(score); obs.disconnect(); return; }
          const start = performance.now();
          const dur = 1400;
          const tick = (now) => {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setAnimated(Math.round(eased * score));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
          obs.disconnect();
        }
      });
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [score]);

  const offset = circumference - (animated / 100) * circumference;

  return (
    <div ref={ref} className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="wsgrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#10C98A" />
            <stop offset="100%" stopColor="#9EF2CF" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#172031" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#wsgrad)"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.1s linear" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-6xl font-bold text-[#F5F7FB]">{animated}</span>
        <span className="text-sm text-[#8290A8] mt-1">WorthScore</span>
        <span className="text-[11px] text-[#8290A8]/70 mt-0.5">Example data</span>
      </div>
    </div>
  );
};

const Bar = ({ item, show }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-[#F5F7FB]">{item.label}</span>
      <span className="text-sm font-semibold" style={{ color: stateColor[item.state] }}>{item.value}</span>
    </div>
    <div className="h-2 rounded-full bg-[#172031] overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{
          width: show ? `${item.value}%` : "0%",
          background: stateColor[item.state],
          transition: "width 1s cubic-bezier(0.22,1,0.36,1)",
        }}
      />
    </div>
  </div>
);

const WorthScore = () => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setShow(true); obs.disconnect(); } });
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="worthscore" className="relative py-20 lg:py-28 bg-[#0A1220]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-[13px] font-semibold tracking-wide text-[#13D99A] uppercase mb-4">WorthScore</p>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-[#F5F7FB] leading-[1.08]">
            {WORTHSCORE.headline}
          </h2>
          <p className="mt-5 text-lg text-[#8290A8] leading-relaxed">{WORTHSCORE.copy}</p>
        </Reveal>

        <div ref={ref} className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute -inset-8 bg-[#10C98A]/10 blur-3xl rounded-full -z-10" />
              <ScoreRing score={WORTHSCORE.score} />
            </div>
          </div>
          <div className="space-y-5">
            {WORTHSCORE.categories.map((c) => (
              <Bar key={c.label} item={c} show={show} />
            ))}
            <p className="text-sm text-[#8290A8] pt-2">{WORTHSCORE.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorthScore;
